// Copying one trip DAY into the Captain's Log.
//
// A TaskPogoTrip leg is a day you plan to run; a Captain's Log
// TaskPogoCompletedTask entry is a day you ran. The two models already agree on
// everything that identifies the day -- both carry a trip binding through
// TripIdentity.mjs, both carry gazetteer-backed From/To, and both carry a route
// in the reserved `fields` blob -- so this is a field mapping, not a
// transformation. Built the way LogbookIO's entriesToCreateInputs builds a
// create input from a foreign record.
//
// Three things are deliberately NOT copied:
//
//   * `distance`. An entry stores no distance -- it is measured from the route
//     wherever it is shown, so there is no second copy to drift. Copying the
//     leg's number would create exactly the copy that rule exists to prevent.
//   * `days`. A leg with days > 1 is a STAY, not several passages. It yields
//     one entry, dated the leg's date. Do not fan out into N entries.
//   * `name`. Not a Captain's registry key (its titleColumns are from/to), so
//     splitEntryFields would drop it anyway. Same as an imported entry.
//
// And one is deliberately not SET: `captain` stays blank, which means the
// owner (LogbookTypes.mjs) -- a leg has no captain, and the owner is the one
// planning the trip.
//
// The route is copied VERBATIM -- no resolveRouteAgainstPlaces. This is a copy
// within one environment, so the waypoints' `placeId`s already name rows that
// exist; the import path resolves only because it crosses environments. It
// still goes through routeFromTrip rather than reaching into the blob, because
// TripRoute.mjs is the blob's single choke point and is what drops malformed
// waypoints and refuses an unknown version.

import { LOGBOOK_TYPE_CAPTAINS, splitEntryFields } from './LogbookTypes.mjs';
import { ENTRY_ROUTE_KEY, routeFromTrip, mergeRouteIntoFields } from './TripRoute.mjs';
import { tripIdentityDefaults } from './TripIdentity.mjs';

// A create input for the CompletedTask that records this leg.
//
// `itemId` and `logbookId` are the caller's: splitEntryFields emits only
// registry keys, so identity keys are always the caller's to re-attach (see its
// header -- owner/createdAt/Task are not on the mutation input, and AppSync
// rejects the whole mutation if one appears).
//
// Database.withoutNullIndexKeys serializes the plain-object `fields` to an
// AWSJSON string when the mutation runs; it is left an object here.
export function legToEntryInput(leg, { itemId, logbookId } = {}) {
  // The trip binding goes through tripIdentityDefaults rather than copying the
  // two columns by hand: that is the one place "never stamp an id without a
  // name" is applied at the write end, so a blank-named leg yields an entry
  // with neither key rather than an orphaned id.
  const identity = tripIdentityDefaults({ name: leg?.name, tripGroupId: leg?.tripGroupId });

  const flat = {
    date: leg?.date,
    from: leg?.from,
    to: leg?.to,
    notes: leg?.notes,
    // Counters and hours are what only exist after the fact -- the whole reason
    // the copy opens the new entry rather than just creating it.
    includeInSummary: true,
  };
  if (identity.name !== undefined) flat.tripName = identity.name;
  if (identity.tripGroupId !== undefined) flat.tripGroupId = identity.tripGroupId;

  // No `preserve`: this is a brand-new row, so there is no prior blob whose
  // unregistered keys have to survive.
  const split = splitEntryFields(flat, LOGBOOK_TYPE_CAPTAINS);

  // fields.route on a leg, fields.geoRoute on an entry. Different keys because
  // the Pilot's registry already owns fields.route as free text.
  const route = routeFromTrip(leg);
  if (route) {
    split.fields = mergeRouteIntoFields(split.fields, route, ENTRY_ROUTE_KEY);
  }

  return { ...split, itemId, logbookId };
}
